import React, { useState, useEffect } from 'react';
import { journalAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { 
  Loader2, 
  BookOpen,
  BarChart2,
  PieChart
} from 'lucide-react';
import { format } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
);

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

const JournalStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await journalAPI.getStats();
        if (response.data) {
          setStats(response.data);
        }
      } catch (err) {
        console.error('Error fetching journal stats:', err);
        setError('Failed to load journal statistics');
        toast.error('Failed to load journal statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="ml-3 text-gray-600 dark:text-gray-300">Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center text-red-600 dark:text-red-400">
        <p>{error}</p>
      </div>
    );
  }

  // If backend returns wrapped payload {stats, entries_by_type, monthly_entries, ...}, normalize
  const normalized = stats && stats.stats ? stats : {
    stats,
    entries_by_type: stats?.entries_by_type || {},
    monthly_entries: stats?.monthly_entries || [],
    mood_distribution: stats?.mood_distribution || {},
  };

  if (!normalized || (normalized.stats?.total_entries ?? 0) === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No journal entries yet to display statistics</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
          Start creating journal entries to see your statistics!
        </p>
      </div>
    );
  }

  // Prepare chart data
  const monthlyData = {
    labels: normalized.monthly_entries?.map(item => format(new Date(item.month), 'MMM')) || [],
    datasets: [{
      label: 'Entries',
      data: normalized.monthly_entries?.map(item => item.count) || [],
      backgroundColor: '#3B82F6',
      borderColor: '#3B82F6',
      borderWidth: 1,
    }]
  };

  const entryTypeData = {
    labels: Object.entries(normalized.entries_by_type || {})
      .filter(([, count]) => count > 0)
      .map(([type]) => type.replace('_', ' ').toUpperCase()),
    datasets: [{
      data: Object.entries(normalized.entries_by_type || {})
        .filter(([, count]) => count > 0)
        .map(([, count]) => count),
      backgroundColor: COLORS.slice(0, Object.keys(normalized.entries_by_type || {}).length),
      borderWidth: 1,
    }]
  };

  const moodDistributionData = {
    labels: Object.entries(normalized.mood_distribution || {})
      .filter(([, count]) => count > 0)
      .map(([mood]) => mood.charAt(0).toUpperCase() + mood.slice(1).toLowerCase()),
    datasets: [{
      data: Object.entries(normalized.mood_distribution || {})
        .filter(([, count]) => count > 0)
        .map(([, count]) => count),
      backgroundColor: COLORS.slice(0, Object.keys(normalized.mood_distribution || {}).length),
      borderWidth: 1,
    }]
  };

  // StatCard removed per request

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Professional summary header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 flex items-center">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg"><BarChart2 className="h-5 w-5 text-blue-600 dark:text-blue-400"/></div>
          <div className="ml-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Entries</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{normalized.stats?.total_entries ?? 0}</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 flex items-center">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg"><PieChart className="h-5 w-5 text-emerald-600 dark:text-emerald-400"/></div>
          <div className="ml-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Active Month</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{(normalized.monthly_entries?.slice(-1)[0]?.count) ?? 0}</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 flex items-center">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg"><BookOpen className="h-5 w-5 text-purple-600 dark:text-purple-400"/></div>
          <div className="ml-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Most Common Type</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{Object.entries(normalized.entries_by_type || {}).sort((a,b)=>b[1]-a[1])[0]?.[0]?.toUpperCase() || '—'}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Entries Chart */}
        <ChartCard title="Entries by Month">
          <div className="h-80">
            <Bar 
              data={monthlyData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: { beginAtZero: true, ticks: { color: '#6B7280' }, grid: { color: '#374151' } },
                  x: { ticks: { color: '#6B7280' }, grid: { color: '#374151' } }
                }
              }} 
            />
          </div>
        </ChartCard>

        {/* Entry Types Chart */}
        <ChartCard title="Entries by Type">
          <div className="h-80">
            <Pie 
              data={entryTypeData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { color: '#6B7280' } } }
              }} 
            />
          </div>
        </ChartCard>

        {/* Mood Distribution Chart */}
        <ChartCard title="Mood Distribution">
          <div className="h-80">
            <Pie 
              data={moodDistributionData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { color: '#6B7280' } } }
              }} 
            />
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default JournalStats;